import { Component, EventEmitter, Input, Output, ViewChild, AfterViewInit, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormControl } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatIconModule } from '@angular/material/icon';
import { MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';

export interface ComboOption {
  value: string;
  label: string;
  icon?: string;
}

@Component({
  selector: 'app-combobox',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatAutocompleteModule,
    MatIconModule,
  ],
  template: `
    <mat-form-field appearance="outline" class="w-100 compact">
      <mat-icon matPrefix>search</mat-icon>
      <input type="text"
             matInput
             [placeholder]="placeholder || 'Seleccionar'"
             [formControl]="inputCtrl"
             [matAutocomplete]="auto"
             (focus)="openPanel()"
             (click)="openPanel()" />
      <button mat-icon-button matSuffix *ngIf="(inputCtrl.value || '').length" aria-label="Limpiar" (click)="clear($event)">
        <mat-icon>close</mat-icon>
      </button>
      <mat-autocomplete #auto="matAutocomplete" class="combo-panel" [panelWidth]="'calc(100% - 2px)'" (optionSelected)="onOptionSelected($event.option.value)">
        <mat-option *ngFor="let opt of filtered$ | async" [value]="opt.label">
          <div class="opt">
            <mat-icon *ngIf="opt.icon" class="mr-6">{{opt.icon}}</mat-icon>
            <span>{{ opt.label }}</span>
          </div>
        </mat-option>
      </mat-autocomplete>
    </mat-form-field>
  `,
  styles: [`
    :host { display: block; width: 100%; overflow: hidden; padding-inline: 1px; box-sizing: border-box; }
    :host ::ng-deep .mat-mdc-form-field { width: 100%; margin: 0; box-sizing: border-box; overflow: hidden; }
    :host ::ng-deep .mat-mdc-form-field-flex { width: 100%; box-sizing: border-box; }
    :host ::ng-deep .mdc-text-field { width: 100%; box-sizing: border-box; }
    :host ::ng-deep .mdc-text-field--outlined { 
      --mdc-outlined-text-field-outline-width: 1px; /* normal */
      --mdc-outlined-text-field-focus-outline-width: 1px; /* lock on focus to avoid growth */
    }
    :host ::ng-deep .mdc-notched-outline { transition: none !important; }
    .compact .mdc-text-field--outlined { height: 32px; }
    .compact .mat-mdc-form-field-infix { padding-top: 2px; padding-bottom: 2px; }
    .compact .mat-mdc-input-element { font-size: 12px; }
    .w-100 { width: 100%; }
    .opt { display:flex; align-items:center; gap:8px; }
    .mr-6 { margin-right: 6px; }
    :host ::ng-deep .combo-panel.mat-mdc-autocomplete-panel { max-height: 70vh !important; width: 100% !important; min-width: 0 !important; }
  `]
})
export class ComboboxComponent implements AfterViewInit, OnChanges {
  @Input() options: ComboOption[] = [];
  @Input() placeholder = 'Buscar';
  @Input() showAllOnOpen = true;
  // Permite fijar la selección desde el padre (por value) y reflejar el label
  @Input() selectedValue: string | null = null;
  @Output() selected = new EventEmitter<ComboOption>();
  @Output() cleared = new EventEmitter<void>();
  @ViewChild(MatAutocompleteTrigger) trigger!: MatAutocompleteTrigger;

  inputCtrl = new FormControl('');
  filtered$: Observable<ComboOption[]> = this.inputCtrl.valueChanges.pipe(
    startWith(''),
    map(val => this.filter(val || ''))
  );

  private filter(val: string): ComboOption[] {
    const v = (val || '').toLowerCase();
    return this.options.filter(o => (o.label || '').toLowerCase().includes(v));
  }

  onOptionSelected(label: string) {
    const found = this.options.find(o => o.label === label);
    // keep label in the input and emit selection
    this.inputCtrl.setValue(label, { emitEvent: false });
    if (found) this.selected.emit(found);
    if (this.trigger?.panelOpen) this.trigger.closePanel();
  }

  ngAfterViewInit(): void {
    // Do not auto-open; open only on focus/click
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Cuando cambian las opciones, forzar re-evaluación del filtro para refrescar la lista
    if (changes['options']) {
      const cur = (this.inputCtrl.value || '').toString();
      this.inputCtrl.setValue(cur); // emite y recalcula con this.options actualizadas
    }
    // Si el padre fija selectedValue, mostrar el label correspondiente
    if (changes['selectedValue']) {
      const val = (this.selectedValue || '').toString();
      const opt = this.options.find(o => o.value === val);
      const label = opt?.label || '';
      this.inputCtrl.setValue(label, { emitEvent: false });
    }
  }

  openPanel() {
    // Fuerza un re-render del listado con las opciones actuales
    const cur = (this.inputCtrl.value || '').toString();
    if (this.showAllOnOpen && cur.length) {
      // Clear filter to show full list upon opening
      this.inputCtrl.setValue('');
    } else {
      // Emitir aunque esté vacío para refrescar resultados
      this.inputCtrl.setValue(cur);
    }
    if (this.trigger && !this.trigger.panelOpen) {
      this.trigger.openPanel();
    }
  }

  clear(evt: MouseEvent) {
    evt.stopPropagation();
    this.inputCtrl.setValue('');
    this.cleared.emit();
    this.openPanel();
  }
}
